import { useEffect, useRef, useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { useToast } from "@/hooks/use-toast";
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle2,
  Trophy,
  Users,
  Sparkles,
} from "lucide-react";

type FasciaEta = "" | "bambino" | "ragazzo" | "adulto";
type Categoria = "" | "non-agonismo" | "agonismo";

interface FormState {
  nome: string;
  cognome: string;
  telefono: string;
  email: string;
  fascia_eta: FasciaEta;
  categoria: Categoria;
  note: string;
}

const PAGE_URL = "https://taekwondomattei.com/stagione-2026-2027/";

const trackEvent = (event: string, params: Record<string, unknown> = {}) => {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event, ...params });
  if (typeof w.gtag === "function") w.gtag("event", event, params);
};

const StagioneIscrizioni = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<FormState>({
    nome: "",
    cognome: "",
    telefono: "",
    email: "",
    fascia_eta: "",
    categoria: "",
    note: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formStartedRef = useRef(false);

  useEffect(() => {
    trackEvent("iscrizione_form_view", { page: "/stagione-2026-2027" });
  }, []);

  const handleFirstFocus = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackEvent("iscrizione_form_start");
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(PAGE_URL);
      toast({ title: "Link copiato", description: PAGE_URL });
    } catch {
      toast({ title: "Impossibile copiare", variant: "destructive" });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.nome.trim() ||
      !formData.cognome.trim() ||
      !formData.telefono.trim() ||
      !formData.fascia_eta ||
      !formData.categoria
    ) {
      toast({
        title: "Compila i campi obbligatori",
        description: "Nome, cognome, telefono, fascia d'età e categoria sono richiesti.",
        variant: "destructive",
      });
      return;
    }

    const telClean = formData.telefono.replace(/\s/g, "");
    if (telClean.length < 6 || telClean.length > 20) {
      toast({ title: "Telefono non valido", variant: "destructive" });
      return;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      toast({ title: "Email non valida", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.functions.invoke("send-trial-lesson", {
        body: {
          nome: formData.nome.trim(),
          cognome: formData.cognome.trim(),
          telefono: formData.telefono.trim(),
          email: formData.email.trim() || undefined,
          fascia_eta: formData.fascia_eta,
          orario: "indifferente",
          categoria: formData.categoria === "agonismo" ? "Agonismo" : "Non agonismo",
          messaggio: formData.note.trim() || undefined,
          source: "iscrizioni-2026-2027",
        },
      });

      if (error) throw error;
      if (data && !data.success) throw new Error(data.error || "Errore sconosciuto");

      trackEvent("iscrizione_form_submit", {
        fascia_eta: formData.fascia_eta,
        categoria: formData.categoria,
      });

      setSubmitted(true);
      toast({
        title: "Richiesta di iscrizione inviata!",
        description: "Ti contatteremo entro 24 ore per completare l'iscrizione.",
      });
      setFormData({
        nome: "",
        cognome: "",
        telefono: "",
        email: "",
        fascia_eta: "",
        categoria: "",
        note: "",
      });
    } catch (err) {
      console.error("Submit error:", err);
      toast({
        title: "Errore nell'invio",
        description: "Non è stato possibile inviare la richiesta. Riprova o chiamaci direttamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const eventSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: "Stagione sportiva 2026/2027 – Taekwondo Mattei Roma",
    description:
      "Iscrizioni aperte alla stagione sportiva 2026/2027 del Taekwondo Mattei Roma. Corsi per bambini, ragazzi e adulti, percorso non agonistico e agonistico. Inizio corsi a settembre.",
    startDate: "2026-09-01",
    endDate: "2027-06-30",
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: "Scuola Mazzacurati – Taekwondo Mattei Roma",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Via Marino Mazzacurati, 90",
        addressLocality: "Roma",
        postalCode: "00148",
        addressRegion: "RM",
        addressCountry: "IT",
      },
    },
    organizer: {
      "@type": "SportsOrganization",
      name: "Taekwondo Mattei Roma",
      url: "https://taekwondomattei.com/",
      telephone: "+39 333 334 6514",
      email: "taekwondomatteiroma@gmail.com",
    },
    url: PAGE_URL,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://taekwondomattei.com/" },
      {
        "@type": "ListItem",
        position: 2,
        name: "Stagione 2026/2027",
        item: PAGE_URL,
      },
    ],
  };

  return (
    <Layout>
      <SEO
        title="Iscrizioni Stagione 2026/2027 - Taekwondo Mattei Roma"
        description="Iscrizioni aperte alla stagione 2026/2027 del Taekwondo Mattei Roma. Corsi per bambini, ragazzi e adulti — percorso agonistico e non. Da settembre, Lun/Mer/Ven presso la scuola Mazzacurati."
        canonical={PAGE_URL}
        ogType="website"
        schemaJsonLd={[eventSchema, breadcrumbSchema]}
      />

      {/* Hero */}
      <section className="py-16 md:py-24 gradient-black">
        <div className="container-wide mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold text-sm font-body mb-6">
                <Sparkles className="h-3.5 w-3.5" /> Iscrizioni aperte · Da settembre 2026
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gold mb-6 leading-tight">
                Stagione 2026/2027 – Iscriviti all'Accademia
              </h1>
              <p className="font-body text-lg md:text-xl text-gold/80 mb-8">
                Corsi di Taekwondo per bambini, ragazzi e adulti. Percorso non agonistico e
                agonistico, in una scuola affiliata FITA con oltre 30 anni di storia.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-5 rounded-xl bg-black/40 border border-gold/30">
                  <div className="flex items-center gap-2 text-gold mb-2">
                    <Calendar className="h-5 w-5" />
                    <span className="font-display font-semibold">Giorni</span>
                  </div>
                  <p className="font-body text-white text-sm">
                    Lunedì · Mercoledì · Venerdì
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-black/40 border border-gold/30">
                  <div className="flex items-center gap-2 text-gold mb-2">
                    <Clock className="h-5 w-5" />
                    <span className="font-display font-semibold">Orari</span>
                  </div>
                  <p className="font-body text-white text-sm">
                    Piccoli 17:00–18:15<br />
                    Grandi 18:15–20:00
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-black/40 border border-gold/30 sm:col-span-2">
                  <div className="flex items-center gap-2 text-gold mb-2">
                    <MapPin className="h-5 w-5" />
                    <span className="font-display font-semibold">Sede</span>
                  </div>
                  <p className="font-body text-white text-sm">
                    Via Marino Mazzacurati, 90 – 00148 Roma (RM)<br />
                    Presso la scuola Mazzacurati
                  </p>
                </div>
              </div>
            </div>

            {/* Form card */}
            <div id="iscrizione" className="card-elevated p-6 md:p-8 border border-gold/20 bg-background rounded-xl">
              {submitted ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-14 w-14 text-gold mx-auto mb-4" />
                  <h2 className="font-display text-2xl font-bold text-foreground mb-3">
                    Richiesta ricevuta
                  </h2>
                  <p className="font-body text-muted-foreground mb-6">
                    Ti contatteremo entro 24 ore al numero indicato per completare l'iscrizione alla
                    stagione 2026/2027.
                  </p>
                  <button type="button" className="btn-accent" onClick={() => setSubmitted(false)}>
                    Invia un'altra richiesta
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" onFocus={handleFirstFocus}>
                  <h2 className="font-display text-2xl font-bold text-foreground mb-1">
                    Richiedi l'iscrizione
                  </h2>
                  <p className="font-body text-sm text-muted-foreground mb-2">
                    Compila il modulo: ti contatteremo entro 24 ore.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="nome" className="block font-body text-sm font-medium text-foreground mb-2">
                        Nome *
                      </label>
                      <input
                        type="text"
                        id="nome"
                        name="nome"
                        required
                        maxLength={100}
                        autoComplete="given-name"
                        value={formData.nome}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background font-body text-foreground focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="Mario"
                      />
                    </div>
                    <div>
                      <label htmlFor="cognome" className="block font-body text-sm font-medium text-foreground mb-2">
                        Cognome *
                      </label>
                      <input
                        type="text"
                        id="cognome"
                        name="cognome"
                        required
                        maxLength={100}
                        autoComplete="family-name"
                        value={formData.cognome}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background font-body text-foreground focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="Rossi"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="telefono" className="block font-body text-sm font-medium text-foreground mb-2">
                        Telefono *
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        required
                        inputMode="tel"
                        autoComplete="tel"
                        value={formData.telefono}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background font-body text-foreground focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="+39 333 123 4567"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block font-body text-sm font-medium text-foreground mb-2">
                        Email (facoltativo)
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        maxLength={255}
                        autoComplete="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background font-body text-foreground focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="mario@esempio.it"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="fascia_eta" className="block font-body text-sm font-medium text-foreground mb-2">
                        Fascia d'età *
                      </label>
                      <select
                        id="fascia_eta"
                        name="fascia_eta"
                        required
                        value={formData.fascia_eta}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background font-body text-foreground focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                      >
                        <option value="">Seleziona</option>
                        <option value="bambino">Bambino (4-13)</option>
                        <option value="ragazzo">Ragazzo (14-17)</option>
                        <option value="adulto">Adulto (18+)</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="categoria" className="block font-body text-sm font-medium text-foreground mb-2">
                        Categoria *
                      </label>
                      <select
                        id="categoria"
                        name="categoria"
                        required
                        value={formData.categoria}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background font-body text-foreground focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                      >
                        <option value="">Seleziona</option>
                        <option value="non-agonismo">Non agonismo</option>
                        <option value="agonismo">Agonismo</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="note" className="block font-body text-sm font-medium text-foreground mb-2">
                      Note (facoltativo)
                    </label>
                    <textarea
                      id="note"
                      name="note"
                      rows={4}
                      maxLength={1000}
                      value={formData.note}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background font-body text-foreground focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent resize-y"
                      placeholder="Esperienza pregressa, richieste particolari, domande..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-accent text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Invio in corso..." : "Invia richiesta di iscrizione"}
                  </button>

                  <p className="font-body text-xs text-muted-foreground text-center">
                    Inviando il modulo accetti la nostra{" "}
                    <a href="/privacy-policy" className="text-gold underline underline-offset-2">
                      Privacy Policy
                    </a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Percorsi */}
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              Due percorsi, un'unica accademia
            </h2>
            <p className="font-body text-muted-foreground">
              Scegli il percorso più adatto: crescita personale o competizione, con la stessa cura
              tecnica e umana.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="p-6 md:p-8 rounded-xl bg-secondary/40 border border-gold/20">
              <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-gold" />
              </div>
              <h3 className="font-display text-2xl font-bold text-foreground mb-3">Non agonismo</h3>
              <p className="font-body text-muted-foreground mb-4">
                Il percorso per chi cerca crescita motoria, disciplina, autostima e divertimento.
                Adatto a bambini, ragazzi e adulti di ogni livello, principianti compresi.
              </p>
              <ul className="space-y-2 font-body text-sm text-muted-foreground">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" /> Tecnica di base, forme, controllo del corpo</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" /> Esami di passaggio di cintura</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" /> Ambiente sicuro, gruppi omogenei per età</li>
              </ul>
            </div>

            <div className="p-6 md:p-8 rounded-xl bg-secondary/40 border border-gold/20">
              <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-4">
                <Trophy className="h-6 w-6 text-gold" />
              </div>
              <h3 className="font-display text-2xl font-bold text-foreground mb-3">Agonismo</h3>
              <p className="font-body text-muted-foreground mb-4">
                Il percorso competitivo per chi vuole gareggiare: preparazione atletica dedicata,
                combattimento e partecipazione a gare regionali, nazionali e internazionali.
              </p>
              <ul className="space-y-2 font-body text-sm text-muted-foreground">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" /> Sparring e preparazione atletica</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" /> Coaching in gara con maestri qualificati</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" /> Circuito FITA: interregionali, italiani, europei</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

    </Layout>
  );
};

export default StagioneIscrizioni;
